import Loading, { LoadingProps } from "@components/LSuspense/Loading";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { theme } from "@theme";
import React, { Suspense, useEffect } from "react";
import Animated, { FadeIn } from "react-native-reanimated";
import LIcon from "@components/LIcon";
import LBox from "@components/LBox";
import LText from "@components/LText";
import LButton from "@components/LButton";
import { Try } from "expo-router/build/views/Try";
import { ViewStyle, StyleProp } from "react-native";
import { captureException } from "@utils/captureException";
import { router } from "expo-router";
import type { ComponentType } from "react";
import { getSuspenseErrorCaptureChannel } from "@utils/suspenseErrorHandling";
import { presentIntercomSupport } from "@utils/presentIntercomSupport";
import { ARTIST_PROFILE_UNAVAILABLE_REASONS } from "@libs/wire";

/**
 * The API tags an expected miss with `data.reason` (see the tRPC
 * errorFormatter). An artist the catalogue cannot serve to this visitor is
 * not an error to retry or report: render "not available" with a way back.
 */
export const getUnavailableReason = (error: unknown): string | null => {
  const reason = (error as { data?: { reason?: unknown } } | null)?.data
    ?.reason;
  return typeof reason === "string" &&
    ARTIST_PROFILE_UNAVAILABLE_REASONS.has(
      reason as Parameters<typeof ARTIST_PROFILE_UNAVAILABLE_REASONS.has>[0],
    )
    ? reason
    : null;
};

const UnavailableView = () => (
  <LBox alignItems="center" justifyContent="center">
    <LBox
      alignItems="center"
      gap="s3"
      marginTop="xl1"
      maxWidth={300}
      style={{ marginHorizontal: "auto" }}
    >
      <LIcon name="modal_cross" size={44} />
      <LText variant="messageTitle">Not available</LText>
      <LText variant="messageBody">This artist profile is unavailable.</LText>
    </LBox>
    <LBox
      gap="s4"
      mt="m1"
      justifyContent="center"
      alignItems="center"
      flexDirection="row"
    >
      <LButton
        onPress={() =>
          router.canGoBack() ? router.back() : router.replace("/")
        }
        label="Back"
        sentryEventName="collection_unavailable_back"
        mt="s4"
        variant="tertiary-small"
      />
      <LButton
        onPress={() => router.replace("/")}
        label="Home"
        sentryEventName="collection_unavailable_home"
        mt="s4"
        variant="tertiary-small"
      />
    </LBox>
  </LBox>
);

const DEFAULT_CONFIG = {
  fadeOut: {
    duration: 350,
  },
};

type Config = LoadingProps & typeof DEFAULT_CONFIG;

type LSuspenseProps = {
  children: React.ReactNode;
  config?: Config;
  errorTitle?: string;
  errorBody?: string;
  fallback?: React.ReactNode;
  contentContainerStyle?: StyleProp<ViewStyle>;
};

const SuspenseErrorView = ({
  onPress,
  error,
  title = "Something went wrong...",
  body = "Please try again later",
}: {
  title?: string;
  body?: string;
  error?: any;
  onPress: () => void;
}) => {
  useEffect(() => {
    if (error) {
      captureException(error, {
        channel: getSuspenseErrorCaptureChannel(error),
        area: "error-boundary",
        operation: "suspense",
      });
    }
  }, [error]);

  return (
    <LBox alignItems="center" justifyContent="center">
      <LBox
        alignItems="center"
        gap="s3"
        marginTop="xl1"
        maxWidth={300}
        style={{ marginHorizontal: "auto" }}
      >
        <LIcon name="modal_cross" size={44} />
        <LText variant="messageTitle">{title}</LText>
        <LText variant="messageBody">{body}</LText>
      </LBox>
      <LBox
        gap="s4"
        mt="m1"
        justifyContent="center"
        alignItems="center"
        flexDirection="row"
      >
        <LButton
          onPress={onPress}
          label="Retry"
          sentryEventName="collection_error_boundary_retry"
          mt="s4"
          variant="tertiary-small"
        />
        <LButton
          onPress={() => router.replace("/")}
          label="Home"
          sentryEventName="collection_error_boundary_home"
          mt="s4"
          variant="tertiary-small"
        />
        <LButton
          onPress={async () => {
            await presentIntercomSupport("present-intercom-from-suspense");
          }}
          label="Help"
          sentryEventName="collection_error_boundary_help"
          mt="s4"
          variant="tertiary-small"
        />
      </LBox>
    </LBox>
  );
};

const LSuspense = ({
  children,
  config = DEFAULT_CONFIG,
  errorTitle,
  errorBody,
  fallback,
  contentContainerStyle = {
    flex: 1,
    backgroundColor: theme.colors.screenBg,
  },
}: LSuspenseProps) => {
  const TryComponent = Try as unknown as ComponentType<any>;

  return (
    <QueryErrorResetBoundary>
      {({ reset: resetQuery }) => (
        <TryComponent
          catch={({ error, retry: retryRouter }: any) => {
            if (getUnavailableReason(error)) return <UnavailableView />;
            return (
              <SuspenseErrorView
                error={error}
                title={errorTitle}
                body={errorBody}
                onPress={() => {
                  resetQuery();
                  retryRouter().catch((error: unknown) => {
                    captureException(error, {
                      channel: "breadcrumb",
                      area: "navigation",
                      operation: "retry-router",
                    });
                  });
                }}
              />
            );
          }}
        >
          <Suspense
            fallback={
              fallback || (
                <Loading
                  contentContainerStyle={{
                    backgroundColor: theme.colors.screenBg,
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                  }}
                  disableInitialFadeIn
                />
              )
            }
          >
            <Animated.View
              entering={FadeIn.duration(config.fadeOut.duration)}
              style={contentContainerStyle}
            >
              {children}
            </Animated.View>
          </Suspense>
        </TryComponent>
      )}
    </QueryErrorResetBoundary>
  );
};

export default LSuspense;
